import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "../lib/api";
import { formatViews } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import Layout, { Spinner } from "../components/Layout";
import Ticker from "../components/Ticker";
import SectionBlock, { SectionHeader } from "../components/SectionBlock";
import { HeroCard, NewsCard, ListCard } from "../components/NewsCard";
import FounderBlock from "../components/FounderBlock";

function Grid({ items, cols = 4 }) {
  const map = { 3: "md:grid-cols-3", 4: "md:grid-cols-2 lg:grid-cols-4" };
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 ${map[cols]} gap-x-6 gap-y-8`}>
      {items.map((a) => <NewsCard key={a.slug} a={a} />)}
    </div>
  );
}

function TwoColSection({ title, titleHi, to, items }) {
  if (!items?.length) return null;
  return (
    <SectionBlock>
      <SectionHeader title={title} titleHi={titleHi} to={to} />
      <Grid items={items} cols={4} />
    </SectionBlock>
  );
}

// Dark discovery band: only renders columns that actually have genuine content.
function DiscoverySection({ data }) {
  const cols = [
    { key: "trending", title: "Trending", icon: "🔥", items: data.trending, numColor: "text-r24-red" },
    { key: "most_read", title: "Most Read", items: data.most_read, numColor: "text-white/40", showViews: true },
    { key: "fastest_growing", title: "Fastest Growing", icon: "📈", items: data.fastest_growing, numColor: "text-r24-red" },
    { key: "editors_picks", title: "Editor's Picks", icon: "⭐", items: data.editors_picks, thumb: true },
  ].filter((c) => c.items?.length);
  if (!cols.length) return null;
  const grid = { 1: "md:grid-cols-1", 2: "md:grid-cols-2", 3: "md:grid-cols-3", 4: "md:grid-cols-2 lg:grid-cols-4" }[cols.length];
  return (
    <div className="bg-r24-black text-white">
      <SectionBlock>
        <div className={`grid gap-10 ${grid}`}>
          {cols.map((c) => (
            <div key={c.key} data-testid={`discovery-${c.key}`}>
              <h3 className="font-head font-bold text-xl uc mb-4 border-b-2 border-r24-red pb-2">{c.icon ? `${c.icon} ` : ""}{c.title}</h3>
              <div className="flex flex-col">
                {c.items.map((a, i) => (
                  <Link key={a.slug} to={`/article/${a.slug}`} className="flex gap-3 py-3 border-b border-white/10 group items-center">
                    {c.thumb
                      ? (a.featured_image && <img src={a.featured_image} alt="" className="w-16 h-12 object-cover shrink-0" />)
                      : <span className={`font-head text-xl font-bold w-6 ${c.numColor}`}>{i + 1}</span>}
                    <span className="flex-1 min-w-0">
                      <span className={`block font-head font-semibold text-sm leading-snug group-hover:text-red-300 ${a.language === "hi" ? "lang-hi" : ""}`}>{a.title}</span>
                      {c.showViews && formatViews(a.views) && <span className="text-white/50 text-[11px] font-mono2">{formatViews(a.views)} Views</span>}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </SectionBlock>
    </div>
  );
}

export default function Home() {
  const [data, setData] = useState(null);
  useSeo({ description: "Rapid24News — Live news from Lucknow, Uttar Pradesh, India & the World. Breaking news, crime, politics, business, sports. Fast, Trusted, Fearless." });

  useEffect(() => {
    client.get("/homepage").then((r) => setData(r.data)).catch(() => setData({}));
  }, []);

  if (!data) return <Layout><Spinner /></Layout>;

  const top = data.top_stories || [];
  const hero = top[0];
  const sideTop = top.slice(1, 5);

  return (
    <Layout>
      <Ticker items={data.breaking_ticker} />

      {/* TOP STORIES — bento */}
      <SectionBlock className="pt-6">
        <SectionHeader title="Top Stories" titleHi="बड़ी ख़बरें" />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8">{hero && <HeroCard a={hero} />}</div>
          <div className="lg:col-span-4 flex flex-col divide-y divide-gray-200">
            {sideTop.map((a, i) => <div key={a.slug} className={i === 0 ? "" : "pt-1"}><ListCard a={a} /></div>)}
          </div>
        </div>
      </SectionBlock>

      {/* LATEST NEWS — fresh stories always visible, even with 0 views */}
      {data.latest?.length > 0 && (
        <TwoColSection title="Latest News" titleHi="ताज़ा ख़बरें" items={data.latest} />
      )}

      {/* LUCKNOW TODAY — priority #1 */}
      {data.lucknow?.length > 0 && (
        <div className="bg-gray-50 border-y border-gray-200">
          <SectionBlock>
            <div className="flex items-center gap-3 mb-5 border-b-2 border-r24-red pb-2">
              <span className="bg-r24-red text-white font-head font-bold uc text-lg px-3 py-1">Lucknow Today</span>
              <span className="font-hindi text-gray-500">लखनऊ — आज की बड़ी ख़बरें</span>
              <Link to="/category/lucknow" className="ml-auto font-head font-semibold uc text-xs text-gray-600 hover:text-r24-red" data-testid="viewall-lucknow">View All →</Link>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-5">{data.lucknow[0] && <NewsCard a={data.lucknow[0]} />}</div>
              <div className="lg:col-span-7 grid sm:grid-cols-2 gap-x-6 gap-y-6">
                {data.lucknow.slice(1, 5).map((a) => <NewsCard key={a.slug} a={a} compact />)}
              </div>
            </div>
          </SectionBlock>
        </div>
      )}

      <TwoColSection title="Uttar Pradesh" titleHi="उत्तर प्रदेश" to="/category/uttar-pradesh" items={data.uttar_pradesh} />

      {/* CRIME — distinct */}
      {data.crime?.length > 0 && (
        <SectionBlock>
          <SectionHeader title="Crime" titleHi="क्राइम" to="/category/crime" accent="black" />
          <Grid items={data.crime} cols={4} />
          <p className="text-xs text-gray-500 mt-4 italic border-l-2 border-r24-red pl-3">
            Rapid24News responsibly distinguishes allegations, police claims, FIRs and confirmed facts. Allegations are not established facts until proven.
          </p>
        </SectionBlock>
      )}

      {/* INVESTIGATION DESK */}
      {data.investigation?.length > 0 && (
        <SectionBlock>
          <div className="flex items-center gap-3 mb-5 border-b-2 border-black pb-2">
            <span className="bg-black text-white font-head font-bold uc text-lg px-3 py-1">Investigation Desk</span>
            <span className="font-hindi text-gray-500">पड़ताल · विशेष रिपोर्ट</span>
            <Link to="/category/investigation" className="ml-auto font-head font-semibold uc text-xs text-gray-600 hover:text-r24-red" data-testid="viewall-investigation">View All →</Link>
          </div>
          <Grid items={data.investigation} cols={4} />
          <p className="text-xs text-gray-500 mt-4 italic border-l-2 border-black pl-3">
            Rapid24News Investigation Desk reports are based on available documents, complaints and official responses. Allegations are clearly distinguished from confirmed facts.
          </p>
        </SectionBlock>
      )}

      {/* POLITICS + NATIONAL side by side */}
      <SectionBlock>
        <div className="grid md:grid-cols-2 gap-10">
          <div>
            <SectionHeader title="Politics" titleHi="राजनीति" to="/category/politics" />
            <div className="flex flex-col divide-y divide-gray-200">
              {(data.politics || []).map((a, i) => <ListCard key={a.slug} a={a} index={i} />)}
            </div>
          </div>
          <div>
            <SectionHeader title="National" titleHi="देश" to="/category/national" />
            <div className="flex flex-col divide-y divide-gray-200">
              {(data.national || []).map((a, i) => <ListCard key={a.slug} a={a} index={i} />)}
            </div>
          </div>
        </div>
      </SectionBlock>

      <div className="bg-gray-50 border-y border-gray-200">
        <TwoColSection title="International" titleHi="दुनिया" to="/category/international" items={data.international} />
      </div>

      <SectionBlock>
        <div className="grid md:grid-cols-2 gap-10">
          <div>
            <SectionHeader title="Business" titleHi="बिज़नेस" to="/category/business" />
            <Grid items={(data.business || []).slice(0, 2)} cols={3} />
          </div>
          <div>
            <SectionHeader title="Technology" titleHi="टेक" to="/category/technology" />
            <Grid items={(data.technology || []).slice(0, 2)} cols={3} />
          </div>
        </div>
      </SectionBlock>

      <SectionBlock>
        <div className="grid md:grid-cols-2 gap-10">
          <div>
            <SectionHeader title="Sports" titleHi="खेल" to="/category/sports" />
            <Grid items={(data.sports || []).slice(0, 2)} cols={3} />
          </div>
          <div>
            <SectionHeader title="Entertainment" titleHi="मनोरंजन" to="/category/entertainment" />
            <Grid items={(data.entertainment || []).slice(0, 2)} cols={3} />
          </div>
        </div>
      </SectionBlock>

      {/* DISCOVERY — Trending / Most Read / Fastest Growing / Editor's Picks (real signals only) */}
      <DiscoverySection data={data} />

      {/* VIDEO NEWS */}
      {data.videos?.length > 0 && (
        <SectionBlock>
          <SectionHeader title="Rapid24 Video" titleHi="वीडियो" to="/video" />
          <Grid items={data.videos} cols={4} />
        </SectionBlock>
      )}

      <FounderBlock />
    </Layout>
  );
}
